import { useState, useEffect, useCallback } from 'react';
import { PostgrestError } from '@supabase/supabase-js';
import { supabaseData } from '../../utils/supabase-data-client';
import { Note } from '../../types/notes';
import { useOnlineStatus } from '../useOnlineStatus';

interface UseTrashedNotesReturn {
  data: Note[] | undefined;
  loading: boolean;
  error: PostgrestError | null;
  isOnline: boolean;
  refetch: () => Promise<void>;
}

/**
 * Network-first list of trashed (soft-deleted) notes.
 *
 * Trashed notes are intentionally NOT cached (keeps the localStorage blob lean),
 * so this always hits the network and is unavailable offline. Callers should use
 * `isOnline` to render an "unavailable offline" message instead of an empty list.
 */
const useTrashedNotes = (): UseTrashedNotesReturn => {
  const { isOnline } = useOnlineStatus();
  const [data, setData] = useState<Note[] | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<PostgrestError | null>(null);

  const refetch = useCallback(async () => {
    if (!isOnline) {
      setData(undefined);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    const { data: result, error: queryError } = await supabaseData
      .from('notes')
      .select<'*', Note>('*')
      .not('deleted_at', 'is', null)
      .order('deleted_at', { ascending: false });

    if (queryError) {
      setError(queryError);
      setData(undefined);
    } else {
      setData((result as Note[]) ?? []);
    }
    setLoading(false);
  }, [isOnline]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { data, loading, error, isOnline, refetch };
};

export default useTrashedNotes;
